import React from "react";
import styled from "styled-components";

import {
  ColorDescription,
  getColorByDescription,
} from "../CareConnectMobile-Theme";

export interface Props extends React.HTMLAttributes<HTMLElement> {
  /**
   * Other variants of typography
   * @default p
   */
  variant?: "sm" | "p" | "h1" | "h2" | "h3" | "h4";
  /** Where to align the text */
  align?: "left" | "center" | "right";
  /** Color options  for the text */
  color?: ColorDescription;
  /** Weight of the text */
  weight?: "default" | "bold" | "extra-bold" | "light";
  /** Style of text */
  fontStyle?: "normal" | "italic";
  /**
   * Wrap overflow element with ellipsis
   * @default false
   */
  ellipsis?: boolean;
  margin?: string;
  editable?: boolean;
}

const StyledHTMLElement = styled.p<Props>`
  border-bottom: ${(props) => props.editable && "1px dashed"};
  font-size: ${getFontSize};
  font-style: ${getFontStyle}
  font-weight: ${getFontWeight};
  color: ${(props) => getColorByDescription(props, props.color)};
  margin: ${getMargin};
  text-align: ${getTextAlignment};
  ${({ ellipsis }) =>
    ellipsis &&
    `
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;`};
`;

/**
 * Modal view component, does not manage its own state.
 */
const Typography = (props: Props) => {
  return (
    <StyledHTMLElement as={getHTMLElement(props)} {...props}>
      {props.children}
    </StyledHTMLElement>
  );
};

function getFontStyle({ fontStyle }: Props) {
  switch (fontStyle) {
    case "italic":
      return "italic";
    default:
    case "normal":
      return "normal";
  }
}

function getHTMLElement({ variant }: Props) {
  switch (variant) {
    case "h1":
      return "h1";
    case "h2":
      return "h2";
    case "h3":
      return "h3";
    case "h4":
      return "h4";
    case "sm":
    default:
      return "p";
  }
}

function getFontSize({ variant }: Props) {
  switch (variant) {
    case "h1":
      return "2.6rem";
    case "h2":
      return "2.2rem";
    case "h3":
      return "2.0rem";
    case "h4":
      return "1.8rem";
    case "sm":
      return "1.3rem";
    default:
      return "1.6rem";
  }
}

function getFontWeight({ variant, weight, color }: Props) {
  if (weight === "extra-bold") {
    return "800";
  }
  if (weight === "bold" || color === "strong") {
    return "700";
  }
  if (weight === "light") {
    return "300";
  }
  switch (variant) {
    case "h1":
      return "600";
    case "h2":
    case "h3":
    case "h4":
      return "500";
    default:
      return "400";
  }
}

function getMargin({ variant, margin }: Props) {
  switch (variant) {
    case "h1":
    case "h2":
    case "h3":
    case "h4":
      if (margin === "narrow") {
        return "1em 0";
      } else if (margin === "very-narrow") {
        return "0.3em 0";
      }
      return margin ? margin : "2rem 0";
    case "sm":
    case "p":
      if (margin === "narrow") {
        return "0.3em 0";
      }
      return margin ? margin : "0.5em 0";
    default:
      return undefined;
  }
}

function getTextAlignment({ align }: Props) {
  switch (align) {
    case "center":
      return "center !important";
    case "right":
      return "right !important";
    default:
      return undefined;
  }
}

export default Typography;
